import styled from "@emotion/styled";
import { Box, Skeleton } from "@mui/joy";
import { Table as MuiTable } from "@mui/joy";
import { Cell, Row, flexRender } from "@tanstack/react-table";
import { Table as TableType } from "@tanstack/react-table";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import React, { ReactNode, useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";

import { Route } from "@/core/types";
import { useIsKeyDownRef } from "@/hooks/useIsKeyDown";

import TableHeader from "./TableHeader";

const TableCell = ({
  row,
  cell,
  virtualRow,
  handleRowCheckboxChange,
}: {
  row: Row<Route>;
  cell: Cell<Route, unknown>;
  virtualRow: VirtualItem;
  handleRowCheckboxChange: (uuid: string) => void;
}) => {
  return (
    <Styled.TableCell width={cell.column.getSize()} height={`${virtualRow.size}px`}>
      {flexRender(cell.column.columnDef.cell, {
        ...cell.getContext(),
        ...(cell.column.id === "selected"
          ? {
              onCheckboxChange: () => handleRowCheckboxChange(row.id),
            }
          : {}),
      })}
    </Styled.TableCell>
  );
};

const TableRow = ({
  row,
  virtualRow,
  isHighlighted,
  handleRowClick,
  handleRowCheckboxChange,
  onContextMenu,
}: {
  row: Row<Route>;
  virtualRow: VirtualItem;
  isHighlighted: boolean;
  handleRowClick: (uuid: string) => void;
  handleRowCheckboxChange: (uuid: string) => void;
  onContextMenu: (event: React.MouseEvent) => void;
}) => {
  return (
    <Styled.TableRow
      role="checkbox"
      onClick={() => handleRowClick(row.id)}
      isHighlighted={isHighlighted}
      isSelected={row.getIsSelected()}
      height={`${virtualRow.size}px`}
      start={virtualRow.start}
      data-index={virtualRow.index} //needed for dynamic row height measurement
      data-uuid={row.id}
      // ref={(node) => rowVirtualizer.measureElement(node)} //measure dynamic row height
      key={row.id}
      onContextMenu={onContextMenu}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          key={cell.id}
          row={row}
          cell={cell}
          virtualRow={virtualRow}
          handleRowCheckboxChange={handleRowCheckboxChange}
        />
      ))}
    </Styled.TableRow>
  );
};

const RoutesTable = ({
  table,
  endRow,
  endRowHeight,
  isDataPending,
  selection,
  addToSelection,
  removeFromSelection,
  setSelection,
  canChangeSelection,
  onCannotChangeSelection,
  onContextMenu,
  highlightedRouteUuids,
  onEndReached,
}: {
  table: TableType<Route>;
  endRow?: () => ReactNode;
  endRowHeight?: number;
  isDataPending: boolean;
  selection: Set<string>;
  addToSelection: (uuids: string[]) => void;
  removeFromSelection: (uuids: string[]) => void;
  setSelection: (uuids: string[]) => void;
  canChangeSelection: boolean;
  onCannotChangeSelection: () => void;
  onContextMenu: (event: React.MouseEvent) => void;
  highlightedRouteUuids: Set<string>;
  onEndReached?: () => void;
}) => {
  const rows = table.getRowModel().rows;
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: rows.length + (endRow ? 1 : 0),
    getScrollElement: () => tableContainerRef.current,
    estimateSize: useCallback(
      (index: number) => {
        if (index === rows.length && endRowHeight) {
          return endRowHeight;
        }
        return 34;
      },
      [rows, endRowHeight],
    ),
    getItemKey: useCallback((index: number) => rows[index]?.id ?? index, [rows]),
    overscan: 50,
    scrollPaddingStart: 40,
    scrollPaddingEnd: 40,
  });

  const lastSelectedRouteUuid = useMemo(() => Array.from(selection).pop() ?? null, [selection]);

  const selectionChangedWithinTableRef = useRef(false);

  const selectRowsBetween = useCallback(
    (startUuid: string | null, endUuid: string) => {
      const rows = table.getSortedRowModel().rows;
      const startIndex = startUuid ? rows.findIndex((x) => x.id === startUuid) : 0;
      const endIndex = rows.findIndex((x) => x.id === endUuid);
      if (startIndex === -1 || endIndex === -1) {
        return;
      }
      console.log({ startIndex, endIndex, startUuid, endUuid });

      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);

      console.log({ minIndex, maxIndex });
      const selectedRowUuids = rows.slice(minIndex, maxIndex + 1).map((x) => x.id);
      console.log({ selectedRowUuids });
      addToSelection(selectedRowUuids);
    },
    [addToSelection, table],
  );

  const isShiftKeyPressedRef = useIsKeyDownRef("Shift");
  const isCtrlKeyPressedRef = useIsKeyDownRef(
    ["MacIntel", "iPod", "iPhone", "iPad"].includes(navigator.platform) ? "Meta" : "Control",
  );

  const changeSelection = useCallback(
    (uuid: string, isShiftPressed: boolean, replaceSelectionIfNotSelected: boolean) => {
      selectionChangedWithinTableRef.current = true;
      if (!canChangeSelection) {
        onCannotChangeSelection();
        return;
      }
      const isSelectedAlready = selection.has(uuid);
      if (!isShiftPressed) {
        if (replaceSelectionIfNotSelected) {
          if (!isSelectedAlready) {
            setSelection([uuid]);
          }
        } else {
          if (!isSelectedAlready) {
            addToSelection([uuid]);
          } else {
            removeFromSelection([uuid]);
          }
        }
      } else {
        selectRowsBetween(lastSelectedRouteUuid, uuid);
      }
    },
    [
      addToSelection,
      canChangeSelection,
      lastSelectedRouteUuid,
      removeFromSelection,
      selectRowsBetween,
      selection,
      setSelection,
      onCannotChangeSelection,
    ],
  );

  const handleRowClick = useCallback(
    (uuid: string) => {
      if (isCtrlKeyPressedRef.current) {
        changeSelection(uuid, isShiftKeyPressedRef.current, false);
      } else {
        changeSelection(uuid, isShiftKeyPressedRef.current, true);
      }
    },
    [changeSelection, isCtrlKeyPressedRef, isShiftKeyPressedRef],
  );

  const handleRowCheckboxChange = useCallback(
    (uuid: string) => {
      changeSelection(uuid, isShiftKeyPressedRef.current, false);
    },
    [changeSelection, isShiftKeyPressedRef],
  );

  const onEndReachedRef = useRef(onEndReached);
  useLayoutEffect(() => {
    onEndReachedRef.current = onEndReached;
  }, [onEndReached]);

  const onScroll = useCallback(() => {
    if (!tableContainerRef.current) {
      return;
    }
    const { scrollHeight, scrollTop, clientHeight } = tableContainerRef.current;
    if (scrollHeight - scrollTop - clientHeight < 300) {
      onEndReachedRef.current?.();
    }
  }, []);

  const onScrollDebounced = useDebouncedCallback(onScroll, 100);

  return (
    <Box
      ref={tableContainerRef}
      sx={{
        overflow: "auto", //our scrollable table container
        position: "relative", //needed for sticky header
        flex: 1,
        minHeight: 0,
      }}
      onScroll={onEndReached && onScrollDebounced}
    >
      {/* Even though we're still using sematic table tags, we must use CSS grid and flexbox for dynamic row heights */}
      <MuiTable
        borderAxis="bothBetween"
        hoverRow
        size="sm"
        sx={{
          display: "grid",
          flex: 1,
          "--TableCell-selectedBackground": (theme) => theme.vars.palette.primary.softBg,
          "--TableRow-hoverBackground": (theme) => theme.vars.palette.rowHoverBackground,
          ".add-new-row, .add-new-row:hover": {
            backgroundColor: "neutral.100",
          },
          th: {
            backgroundColor: "neutral.100",
          },
          "td, th": {
            paddingX: 1,
            paddingY: 2,
          },
        }}
      >
        <Styled.THead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} style={{ display: "flex", width: "100%" }}>
              {headerGroup.headers.map((header) => {
                return <TableHeader key={header.id} header={header} table={table} />;
              })}
            </tr>
          ))}
        </Styled.THead>
        {isDataPending ? (
          <tbody
            style={{
              display: "grid",
            }}
          >
            {Array(20)
              .fill(0)
              .map((_, i) => (
                <tr key={i} style={{ display: "flex" }}>
                  {table.getVisibleFlatColumns().map((column) => (
                    <td
                      key={column.id}
                      style={{
                        width: column.getSize(),
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Skeleton
                        loading
                        variant="rectangular"
                        width="100%"
                        height="22px"
                        sx={{ display: "block" }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        ) : (
          <tbody
            style={{
              display: "grid",
              height: `${rowVirtualizer.getTotalSize()}px`, //tells scrollbar how big the table is
              position: "relative", //needed for absolute positioning of rows
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              if (virtualRow.index === rows.length && endRow) {
                return (
                  <tr
                    key="endRow"
                    style={{
                      display: "flex",
                      position: "absolute",
                      height: `${endRowHeight}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      width: "100%",
                    }}
                  >
                    <td
                      style={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {endRow()}
                    </td>
                  </tr>
                );
              }
              const row = rows[virtualRow.index];
              return (
                <TableRow
                  key={row.id}
                  row={row}
                  virtualRow={virtualRow}
                  isHighlighted={highlightedRouteUuids.has(row.id)}
                  handleRowClick={handleRowClick}
                  handleRowCheckboxChange={handleRowCheckboxChange}
                  onContextMenu={onContextMenu}
                />
              );
            })}
          </tbody>
        )}
      </MuiTable>
    </Box>
  );
};

const Styled = {
  TableCell: styled.td`
    display: flex;
    width: ${(props) => props.width};
    align-items: center;
    height: ${(props) => props.height};
  `,
  THead: styled.thead`
    display: flex;
    position: sticky;
    top: 0;
    z-index: 10;
  `,
  TableRow: styled.tr<{
    isHighlighted: boolean;
    isSelected: boolean;
    start: number;
    height: string;
  }>`
    display: flex;
    position: absolute;
    height: ${(props) => props.height};
    transform: translateY(${(props) => props.start}px);
    width: 100%;
    ${(props) => (props.isHighlighted ? "background: var(--TableRow-hoverBackground);" : "")}
    ${(props) =>
      props.isSelected
        ? `
      --TableCell-dataBackground: var(--TableCell-selectedBackground);
      --TableCell-headBackground: var(--TableCell-selectedBackground);
    `
        : ""}
  `,
};

export default RoutesTable;
